import { useEffect, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import {
    AlertTriangle,
    Bell,
    ChevronDown,
    ChevronLeft,
    ChevronUp,
    Database,
    LayoutDashboard,
    LayoutGrid,
    Link2,
    MessageSquare,
    Plug,
} from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { AppShell } from '~/components/layout/app-shell'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { cn } from '~/lib/utils'
import { getAssessment, listAssessments } from '~/api/assessment'
import type { AssessmentResponse, MonitoringDashboard } from '~/api/types'
import { getDashboard, linkDashboardAssessment } from '~/api/monitoring'
import { scaleLabel } from '~/components/assessment/labels'
import { AssessmentPicker } from '~/features/monitoring/assessment-picker'
import { AlertsSection } from '~/features/monitoring/alerts-section'
import { AnalysisBoardSection } from '~/features/monitoring/analysis-board-section'
import { AssistantSection } from '~/features/monitoring/assistant-section'
import { DatabaseSection } from '~/features/monitoring/database-section'
import { DeviationsSection } from '~/features/monitoring/deviations-section'
import { OverviewSection } from '~/features/monitoring/overview-section'
import { SettingsSection } from '~/features/monitoring/settings-section'

// 사이드바 항목(사용자 지정 순서). key는 /monitoring/:id/<key> 경로 세그먼트.
const SECTIONS: {
    key: string
    label: string
    icon: ComponentType<{ className?: string }>
}[] = [
    { key: 'board', label: '분석보드', icon: LayoutGrid },
    { key: 'alerts', label: '알림센터', icon: Bell },
    { key: 'deviations', label: '기준 이탈 점검', icon: AlertTriangle },
    { key: 'overview', label: '점검 현황', icon: LayoutDashboard },
    { key: 'assistant', label: 'AI 어시스턴트', icon: MessageSquare },
    { key: 'database', label: '데이터베이스', icon: Database },
    { key: 'settings', label: '데이터 적재', icon: Plug },
]

/** 모니터링 대시보드 상세 — 상단 컨텍스트 헤더(고정) + 좌측 사이드바(고정) + 콘텐츠(내부 스크롤). */
export function MonitoringDashboardPage() {
    const { id, section } = useParams()
    const [dashboard, setDashboard] = useState<MonitoringDashboard | null>(null)
    const [assessment, setAssessment] = useState<AssessmentResponse | null>(
        null
    )
    const [collapsed, setCollapsed] = useState(false)
    // 평가 연결 편집 상태 + 선택지(내 평가 목록).
    const [assessments, setAssessments] = useState<AssessmentResponse[]>([])
    const [editingLink, setEditingLink] = useState(false)
    const [linking, setLinking] = useState(false)

    useEffect(() => {
        if (!id) return
        getDashboard(id)
            .then(setDashboard)
            .catch(() => setDashboard(null))
    }, [id])

    useEffect(() => {
        listAssessments()
            .then(setAssessments)
            .catch(() => setAssessments([]))
    }, [])

    // 대시보드에 연결할 평가를 지정/해제한다('' → 연결 해제).
    const handleLink = async (assessmentId: string) => {
        if (!id) return
        setLinking(true)
        try {
            const updated = await linkDashboardAssessment(
                id,
                assessmentId || null
            )
            setDashboard(updated) // assessmentId 변경 → 아래 effect가 평가 정보 재조회
            setEditingLink(false)
        } finally {
            setLinking(false)
        }
    }

    useEffect(() => {
        const aid = dashboard?.assessmentId
        if (!aid) {
            setAssessment(null)
            return
        }
        getAssessment(aid)
            .then(setAssessment)
            .catch(() => setAssessment(null))
    }, [dashboard?.assessmentId])

    const active =
        SECTIONS.find((s) => s.key === section) ??
        SECTIONS.find((s) => s.key === 'board')!

    const renderSection = (): ReactNode => {
        switch (active.key) {
            case 'settings':
                return <SettingsSection dashboardId={id ?? ''} />
            case 'database':
                return <DatabaseSection dashboardId={id ?? ''} />
            case 'board':
                return <AnalysisBoardSection dashboardId={id ?? ''} />
            case 'deviations':
                return <DeviationsSection dashboardId={id ?? ''} />
            case 'alerts':
                return <AlertsSection dashboardId={id ?? ''} />
            case 'assistant':
                return <AssistantSection dashboardId={id ?? ''} />
            default:
                return <OverviewSection dashboardId={id ?? ''} />
        }
    }

    return (
        <AppShell>
            <div className='flex h-full min-h-0 flex-col'>
                {/* 컨텍스트 헤더 — 항상 보임(접기/펼치기 가능) */}
                <div
                    className={cn(
                        'border-border bg-background relative shrink-0 border-b px-4 lg:px-8 2xl:px-10',
                        collapsed ? 'py-2' : 'py-4'
                    )}>
                    <div className='mx-auto w-full max-w-[1680px]'>
                        {!collapsed ? (
                            <NavLink
                                to='/monitoring'
                                className='text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs'>
                                <ChevronLeft className='size-3.5' />
                                대시보드 목록
                            </NavLink>
                        ) : null}
                        <div
                            className={cn(
                                'flex flex-wrap items-center justify-between gap-3',
                                collapsed ? '' : 'mt-2'
                            )}>
                            <div className='min-w-0'>
                                <h1 className='text-foreground text-lg font-semibold'>
                                    {dashboard?.name ?? '모니터링 대시보드'}
                                </h1>
                                {!collapsed ? (
                                    <div className='text-muted-foreground mt-1.5 flex flex-wrap items-center gap-1.5 text-xs'>
                                        {editingLink ? (
                                            <div className='flex flex-wrap items-center gap-2'>
                                                <div className='w-72'>
                                                    <AssessmentPicker
                                                        assessments={
                                                            assessments
                                                        }
                                                        value={
                                                            dashboard?.assessmentId ??
                                                            ''
                                                        }
                                                        onChange={handleLink}
                                                    />
                                                </div>
                                                <Button
                                                    variant='ghost'
                                                    size='sm'
                                                    onClick={() =>
                                                        setEditingLink(false)
                                                    }
                                                    disabled={linking}>
                                                    취소
                                                </Button>
                                            </div>
                                        ) : (
                                            <>
                                                {assessment ? (
                                                    <>
                                                        <Badge tone='info'>
                                                            {scaleLabel(
                                                                assessment.scale
                                                            )}
                                                        </Badge>
                                                        {assessment.classification ? (
                                                            <Badge tone='accent'>
                                                                {
                                                                    assessment.classification
                                                                }
                                                            </Badge>
                                                        ) : null}
                                                        {assessment.industry ? (
                                                            <span className='max-w-[40ch] truncate'>
                                                                {
                                                                    assessment.industry
                                                                }
                                                            </span>
                                                        ) : null}
                                                    </>
                                                ) : (
                                                    <Badge tone='warning'>
                                                        평가 미연결
                                                    </Badge>
                                                )}
                                                {dashboard?.createdAt ? (
                                                    <span>
                                                        · 생성{' '}
                                                        {dashboard.createdAt.slice(
                                                            0,
                                                            10
                                                        )}
                                                    </span>
                                                ) : null}
                                                <button
                                                    type='button'
                                                    onClick={() =>
                                                        setEditingLink(true)
                                                    }
                                                    className='text-primary hover:bg-accent ml-0.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium transition-colors'>
                                                    <Link2 className='size-3.5' />
                                                    {assessment
                                                        ? '평가 변경'
                                                        : '평가 연결'}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                            <span className='text-muted-foreground shrink-0 text-sm font-medium'>
                                {active.label}
                            </span>
                        </div>
                    </div>
                    <button
                        type='button'
                        onClick={() => setCollapsed((v) => !v)}
                        title={collapsed ? '펼치기' : '접기'}
                        aria-label={collapsed ? '펼치기' : '접기'}
                        className='text-muted-foreground hover:text-foreground absolute right-2 bottom-1 inline-flex size-6 items-center justify-center rounded-md transition-colors'>
                        {collapsed ? (
                            <ChevronDown className='size-4' />
                        ) : (
                            <ChevronUp className='size-4' />
                        )}
                    </button>
                </div>

                {/* 본문 — 사이드바(고정) + 콘텐츠(내부 스크롤) */}
                <div className='mx-auto flex min-h-0 w-full max-w-[1680px] flex-1 gap-5 px-4 py-5 max-md:flex-col lg:px-8 2xl:px-10'>
                    <aside className='w-56 shrink-0 max-md:w-full'>
                        <div className='border-border bg-card rounded-md border p-2'>
                            <nav
                                className='grid gap-1'
                                aria-label='모니터링 메뉴'>
                                {SECTIONS.map((s) => {
                                    const Icon = s.icon
                                    return (
                                        <NavLink
                                            key={s.key}
                                            to={`/monitoring/${id}/${s.key}`}
                                            className={cn(
                                                'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
                                                s.key === active.key
                                                    ? 'bg-accent text-primary font-semibold'
                                                    : 'text-foreground hover:bg-muted/60'
                                            )}>
                                            <Icon className='size-4 shrink-0' />
                                            {s.label}
                                        </NavLink>
                                    )
                                })}
                            </nav>
                        </div>
                    </aside>

                    <div className='min-h-0 min-w-0 flex-1 overflow-y-auto pb-4'>
                        {renderSection()}
                    </div>
                </div>
            </div>
        </AppShell>
    )
}
